import React, { useState, useEffect } from "react";
import { Phone, MessageCircle, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function NavigationOverlay({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  return <div data-section-id="61721" className={cn("fixed inset-0 z-[60] flex flex-col bg-black backdrop-blur-xl transition-all duration-500 ease-in-out", isOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-4")}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-end">
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10 rounded-full size-12 [&_svg]:size-6" aria-label="Fechar menu">
          <X />
        </Button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-start pt-16 px-6">
        <nav className="flex flex-col items-center gap-10 text-center">
          {loading ? <div className="flex flex-col gap-4 animate-pulse">
              <div className="h-8 w-32 bg-white/10 rounded" />
              <div className="h-8 w-40 bg-white/10 rounded" />
              <div className="h-8 w-24 bg-white/10 rounded" />
            </div> : menuItems.map((item, i) => <div key={item.id} data-index={i} className="overflow-hidden">
                <Link to={item.href || "#"} className={cn("block text-4xl md:text-6xl font-bold tracking-tighter text-white hover:text-primary transition-all duration-500 uppercase", isOpen ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")} style={{
            transitionDelay: `${i * 100}ms`
          }} onClick={onClose}>
                  {item.label}
                </Link>
              </div>)}
        </nav>
      </div>

      <div className="p-8 text-center text-white/60 text-sm font-normal">
        <p>© {new Date().getFullYear()} Linx Vila Velha. Todos os direitos reservados.</p>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="cabeçalho-principal" className={cn("dark fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out border-b border-transparent", isScrolled ? "bg-[#1A1A1A]/80 backdrop-blur-md border-white/5 shadow-lg" : "bg-[#1A1A1A]")}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        
        <MenuProvider menu_id="17" className="hidden md:flex items-center justify-center">
          <nav className="flex flex-wrap justify-center gap-x-8 gap-y-4">
            <Link to="/MicrovixPage" className="text-[14px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">Microvix</Link>
            <Link to="/EMillenniumPage" className="text-[14px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">e Millennium</Link>
            <Link to="/DigitalArrowPage" className="text-[14px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">Seta Digital</Link>
            <Link to="/AboutUsPage" className="text-[14px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">Quem Somos</Link>
            <Link to="/ContactsPage" className="text-[14px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">Contatos</Link>
          </nav>
        </MenuProvider>

        <Link to="/HomePage" className="flex-shrink-0 z-50">
          <img src="https://franquialinxvilavelha.com.br/wp-content/uploads/2025/01/SISTEMAS-COMPLETOS-PARA-LOJAS-3-323x88.png" alt="Linx Vila Velha" className="h-[30px] w-auto object-contain" />
        </Link>

        
        <div className="flex items-center gap-4 md:gap-8">
          
          <div className="hidden md:flex items-center gap-4">
            <Button variant="ghost" size="icon" className="bg-[#FF006E] text-white hover:bg-[#FF006E]/90 rounded-[10px] relative group px-4 w-auto h-10 flex gap-2" asChild>
              <Link to="/ContactsPage" aria-label="Contate nosso time">
                <span className="text-sm font-semibold tracking-wide">CONTATE NOSSO TIME</span>
                <Phone className="size-4 group-hover:animate-pulse" />
              </Link>
            </Button>
            
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full" asChild>
              <Link to="https://wa.me/5527992826653" newTab aria-label="Falar no WhatsApp">
                <MessageCircle className="size-5 text-[#25D366]" />
              </Link>
            </Button>
          </div>

          
          <Button variant="ghost" className="h-10 w-12 px-0 hover:bg-transparent flex flex-col items-end justify-center gap-1.5 group cursor-pointer" onClick={() => setIsMenuOpen(true)} aria-label="Abrir menu">
            <span className="h-[2px] w-6 bg-white rounded-full transition-all duration-300 group-hover:w-8 group-hover:bg-[#FF006E]" />
            <span className="h-[2px] w-4 bg-white rounded-full transition-all duration-300 group-hover:w-8 group-hover:bg-[#FF006E]" />
            <span className="h-[2px] w-5 bg-white rounded-full transition-all duration-300 group-hover:w-8 group-hover:bg-[#FF006E]" />
          </Button>
        </div>
      </div>

      
      <MenuProvider menu_id="16">
        <NavigationOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
      </MenuProvider>
    </header>;
}